import React, { useCallback, useEffect, useRef, useState } from 'react'
import 'css/speech.css'

/* Read-aloud built on the browser's own Web Speech API -- the same thing the
 * mockup uses. Deliberately not a paid speech service: this has to work on
 * whatever a school hands a child, offline-ish, with no per-word cost and no
 * student text leaving the device. The trade is that voice quality and
 * availability vary by platform, and some locked-down devices have no voices
 * at all -- which is why `supported` is exposed and every caller hides its
 * button rather than offering something that silently does nothing.
 */

export const TTS_RATE_MIN = 0.5
export const TTS_RATE_MAX = 2.0
const TTS_RATE_STEP = 0.25

const synth = () => (typeof window !== 'undefined' ? window.speechSynthesis : undefined)

export const useSpeech = (defaultRate = 1.0) => {
	const [speaking, setSpeaking] = useState(false)
	const [rate, setRate] = useState(defaultRate)
	const rateRef = useRef(defaultRate)
	const supported = typeof window !== 'undefined' && 'speechSynthesis' in window

	useEffect(() => { rateRef.current = rate }, [rate])

	/* Never leave a voice talking into an empty room: if the teacher advances
	   the slide, navigates away, or the component unmounts mid-sentence, stop. */
	useEffect(() => () => { if (synth()) synth().cancel() }, [])

	const stop = useCallback(() => {
		if (!synth()) return
		synth().cancel()
		setSpeaking(false)
	}, [])

	const speak = useCallback((text) => {
		const s = synth()
		if (!s || !text) return

		if (s.speaking) { stop(); return }

		const utterance = new window.SpeechSynthesisUtterance(text)
		utterance.rate = rateRef.current
		utterance.onstart = () => setSpeaking(true)
		utterance.onend = () => setSpeaking(false)
		utterance.onerror = () => setSpeaking(false)

		/* Chrome returns an empty voice list until voiceschanged fires, so
		   speaking before then can silently do nothing on the very first click
		   -- which reads as a broken button. Same guard the mockup uses. */
		if (s.getVoices().length === 0) {
			const once = () => {
				s.removeEventListener('voiceschanged', once)
				s.speak(utterance)
			}
			s.addEventListener('voiceschanged', once)
			s.getVoices()
			return
		}
		s.speak(utterance)
	}, [stop])

	const adjustRate = useCallback((delta) => {
		setRate(prev => {
			const next = Math.round((prev + delta) * 100) / 100
			return Math.min(TTS_RATE_MAX, Math.max(TTS_RATE_MIN, next))
		})
	}, [])

	return { supported, speaking, speak, stop, rate, adjustRate, step: TTS_RATE_STEP }
}

const SpeakerIcon = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
		<path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
		<path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
	</svg>
)

const StopIcon = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<rect x="6" y="6" width="12" height="12" rx="1.5" />
	</svg>
)

/* The button plus its speed control. The speed control only appears once
   something is being read, matching the mockup -- it is meaningless before
   then and would just be another control to parse. */
export const ReadAloudButton = ({
	getText,
	label = 'Read to me',
	className = '',
	showRate = true,
	speech,
}) => {
	const { supported, speaking, speak, stop, rate, adjustRate, step } = speech

	// A button that cannot speak is worse than no button.
	if (!supported) return null

	return (
		<span className={`tts-row ${className}`}>
			<button
				type="button"
				className={`tts-btn ${speaking ? 'speaking' : ''}`}
				onClick={() => (speaking ? stop() : speak(getText()))}
				aria-label={speaking ? 'Stop reading' : label}
			>
				{speaking ? <StopIcon /> : <SpeakerIcon />}
				{speaking ? 'Stop' : label}
			</button>
			{showRate && speaking && (
				<span className="tts-rate" aria-label="Speech rate">
					<button
						type="button"
						className="tts-rate-btn"
						onClick={() => adjustRate(-step)}
						disabled={rate <= TTS_RATE_MIN}
						aria-label="Slower"
					>&minus;</button>
					<span className="tts-rate-value">{rate.toFixed(1)}&times;</span>
					<button
						type="button"
						className="tts-rate-btn"
						onClick={() => adjustRate(step)}
						disabled={rate >= TTS_RATE_MAX}
						aria-label="Faster"
					>+</button>
				</span>
			)}
		</span>
	)
}
